import { renderHeader } from '../components/header.js';
import { renderNav } from '../components/nav.js';
import { loadWeights, saveWeight, saveCheckin, loadSettings, saveSettings } from '../lib/grit-data.js';
import { bookPlan, twoWeekAverages, daysBetween, BOOK_GOALS, BOOK_ACTIVITY } from '../lib/nutrition.js';
import { localISO } from '../lib/phase.js';
import { viewing, isViewingSelf, label } from '../lib/auth.js';
import { toast } from '../components/toast.js';

// Weight, the Bigger Leaner Stronger way (ch.9 and 15): pick a goal and how much you
// train, get one daily calorie number; weigh in every 1-3 days and judge by the
// 2-week average; check waist and photos every 2 weeks.
const todayISO = () => localISO();
const fmtNum = (n) => Math.round(n).toLocaleString('en-US');
const shortDate = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

export function renderWeight(el) {
  const me = viewing();
  const readOnly = !isViewingSelf();
  let rows = [], settings = {};

  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-4"><p class="text-inksoft">Loading…</p></div>${renderNav('weight')}`;

  (async () => {
    [rows, settings] = await Promise.all([loadWeights(me), loadSettings(me)]);
    paint();
  })();

  const weighIns = () => rows.filter((r) => r.weight_lb != null);
  const latest = () => (weighIns()[0] ? Number(weighIns()[0].weight_lb) : null);
  const goal = () => settings.diet_goal || 'cut';
  const activity = () => settings.activity_level || 'moderate';

  function planCard() {
    const plan = bookPlan({ weightLb: latest(), goal: goal(), activity: activity() });
    const seg = (name, opts, cur) => `<div class="grit-seg">${opts.map(([k, l]) => `<button data-${name}="${k}" class="${cur === k ? 'on' : ''}" ${readOnly ? 'disabled' : ''}>${l}</button>`).join('')}</div>`;
    const act = BOOK_ACTIVITY.find(([k]) => k === activity());
    return `<div class="grit-card p-5">
      <div class="text-[17px] font-semibold text-ink mb-3">Your plan</div>
      ${seg('goal', BOOK_GOALS, goal())}
      <div class="text-[13px] text-inksoft mt-2 mb-4">${goal() === 'cut' ? 'Losing fat. The book starts here if you are about 15% body fat or more (p.148).' : goal() === 'gain' ? 'Building muscle with a small surplus, until about 15-17% body fat (p.153).' : 'Holding steady between a cut and a gain (p.140).'}</div>
      <div class="text-[15px] font-semibold text-ink mb-2">Training each week, cardio included</div>
      ${seg('activity', BOOK_ACTIVITY.map(([k, l]) => [k, l]), activity())}
      <div class="text-[13px] text-inksoft mt-2">${act ? act[2] : ''}. The 5-day program with a little cardio is Moderate (p.138).</div>
      ${plan.ready ? `<div class="bg-pagebg rounded-2xl p-4 mt-4 text-center">
          <div class="font-rounded font-bold text-ink tabular-nums leading-none" style="font-size:40px;letter-spacing:-0.02em">${fmtNum(plan.calories)}</div>
          <div class="text-[15px] text-inksoft mt-1">calories a day · ${plan.perLb} per lb × ${fmtNum(plan.weightLb)} lb</div>
          <div class="text-[15px] text-ink mt-3 tabular-nums">Protein <b>${plan.proteinG} g</b> · Carbs <b>${plan.carbsG} g</b> · Fat <b>${plan.fatG} g</b></div>
        </div>` : '<div class="text-[15px] text-inksoft mt-4">Log a weigh-in below to get your calories.</div>'}
    </div>`;
  }

  function logCard() {
    const lw = latest();
    const loggedToday = weighIns()[0]?.date === todayISO();
    return `<div class="grit-card p-5 mt-3">
      <div class="flex items-center justify-between">
        <div class="text-[17px] font-semibold text-ink">Weigh-in</div>
        ${lw != null ? `<div class="text-[15px] text-inksoft">Latest <b class="text-ink tabular-nums">${lw}</b> lb</div>` : ''}
      </div>
      ${readOnly ? '' : `<div class="flex items-center gap-2 mt-3">
        <input id="wt" type="number" inputmode="decimal" placeholder="${lw != null ? lw : 'lb'}" aria-label="Today's weight in pounds" class="grit-input flex-1 text-center !text-[20px] font-semibold" />
        <button id="logwt" class="grit-btn !w-auto px-7">${loggedToday ? 'Update' : 'Log'}</button>
      </div>`}
      <div class="text-[13px] text-inksoft mt-2">Every 1 to 3 days, first thing in the morning, before you eat (p.341).</div>
    </div>`;
  }

  // Body readings from the scale (iHealth -> Apple Health -> phone shortcut), 2026-10-25.
  function bodyCard() {
    const withBody = rows.filter((r) => r.body_fat_pct != null || r.lean_mass_lb != null || r.bmi != null || r.muscle_mass_lb != null);
    if (!withBody.length) return '';
    const r = withBody[0];
    const prev = withBody.find((x) => x.date < r.date && x.body_fat_pct != null);
    const fatDiff = r.body_fat_pct != null && prev ? Math.round((r.body_fat_pct - prev.body_fat_pct) * 10) / 10 : null;
    const fatLb = r.body_fat_pct != null && r.weight_lb != null ? Math.round((r.weight_lb * r.body_fat_pct) / 10) / 10 : null;
    const cell = (lbl, val, unit) => (val == null ? '' : `<div class="bg-pagebg rounded-2xl p-3"><div class="text-[13px] text-inksoft">${lbl}</div><div class="font-rounded font-bold text-[20px] text-ink tabular-nums mt-0.5">${val}<span class="text-[13px] text-inksoft font-semibold ml-0.5">${unit}</span></div></div>`);
    const when = new Date(r.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `<div class="grit-card p-5 mt-3">
      <div class="flex items-center justify-between"><div class="text-[17px] font-semibold text-ink">Body</div><div class="text-[13px] text-inksoft">${r.source === 'scale' ? 'From your scale' : 'Logged'} · ${when}</div></div>
      <div class="grid grid-cols-2 gap-2 mt-3">
        ${cell('Body fat', r.body_fat_pct, '%')}${cell('Fat', fatLb, 'lb')}${cell('Lean mass', r.lean_mass_lb, 'lb')}${cell('BMI', r.bmi, '')}
        ${cell('Muscle', r.muscle_mass_lb, 'lb')}${cell('Water', r.body_water_pct, '%')}${cell('Bone', r.bone_mass_lb, 'lb')}${cell('Visceral fat', r.visceral_fat, '')}
      </div>
      ${fatDiff != null ? `<div class="text-[13px] text-inksoft mt-2">Body fat ${fatDiff <= 0 ? 'down' : 'up'} ${Math.abs(fatDiff)} points since ${new Date(prev.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}. Scales estimate body fat; watch the trend, not one reading.</div>` : ''}
    </div>`;
  }

  function trendCard() {
    const avgs = twoWeekAverages(weighIns(), todayISO());
    const cur = avgs[avgs.length - 1];
    const prev = avgs[avgs.length - 2];
    const diff = cur && prev ? Math.round((cur.avg - prev.avg) * 10) / 10 : null;
    const head = cur ? `<div class="grit-eyebrow">Average, last 2 weeks</div>
      <div class="flex items-baseline gap-3 mt-1">
        <div class="font-rounded font-bold text-ink tabular-nums leading-none" style="font-size:34px;letter-spacing:-0.02em">${cur.avg}</div>
        ${diff != null ? `<div class="text-[15px] font-semibold ${diff <= 0 ? 'text-success' : 'text-grit'}">${diff <= 0 ? 'Down' : 'Up'} ${Math.abs(diff)} lb</div>` : ''}
      </div>
      ${diff != null ? `<div class="text-[13px] text-inksoft mt-1">Compared with the 2 weeks before (${prev.avg} lb)</div>` : ''}` : '';
    const body = weighIns().length < 2
      ? '<div class="text-[15px] text-inksoft mt-2">A few weigh-ins and your trend shows up here. Daily swings are water and food, so the app judges by 2-week averages.</div>'
      : chartSvg(avgs);
    return `<div class="grit-card p-5 mt-3">${head}${body}</div>`;
  }

  function chartSvg(avgs) {
    const pts = weighIns().map((w) => ({ date: w.date, y: Number(w.weight_lb) })).sort((a, b) => (a.date < b.date ? -1 : 1));
    const start = pts[0].date;
    const end = pts[pts.length - 1].date > todayISO() ? pts[pts.length - 1].date : todayISO();
    const xMax = Math.max(1, daysBetween(start, end));
    const goalW = settings.goal_weight_lb != null && settings.goal_weight_lb !== '' ? Number(settings.goal_weight_lb) : null;
    const ys = [...pts.map((p) => p.y), ...(goalW ? [goalW] : [])];
    const lo = Math.min(...ys), hi = Math.max(...ys);
    const pad = (hi - lo) * 0.12 || 1;
    const yMin = lo - pad, yMax = hi + pad;
    const W = 320, H = 170, padL = 34, padR = 10, padT = 12, padB = 24;
    const X = (iso) => padL + (daysBetween(start, iso) / xMax) * (W - padL - padR);
    const Y = (y) => padT + (1 - (y - yMin) / (yMax - yMin)) * (H - padT - padB);
    const ticks = [...new Set([Math.round(hi), Math.round((lo + hi) / 2), Math.round(lo)])];
    const grid = ticks.map((v) => `<line x1="${padL}" x2="${W - padR}" y1="${Y(v).toFixed(1)}" y2="${Y(v).toFixed(1)}" stroke="rgba(60,60,67,0.12)"/><text x="${padL - 6}" y="${(Y(v) + 4).toFixed(1)}" text-anchor="end" font-size="11" fill="#6E6E73">${v}</text>`).join('');
    const goalLine = goalW != null ? `<line x1="${padL}" x2="${W - padR}" y1="${Y(goalW).toFixed(1)}" y2="${Y(goalW).toFixed(1)}" stroke="#6E6E73" stroke-dasharray="2 4"/><text x="${W - padR}" y="${(Y(goalW) - 5).toFixed(1)}" text-anchor="end" font-size="11" fill="#6E6E73">Goal ${goalW}</text>` : '';
    const dots = pts.map((p) => `<circle cx="${X(p.date).toFixed(1)}" cy="${Y(p.y).toFixed(1)}" r="3" style="fill:rgb(var(--brand) / 0.45)"/>`).join('');
    // The 2-week average: one flat step per period, the line the book says to judge by.
    const steps = avgs.map((a) => {
      const x1 = X(a.startDate < start ? start : a.startDate);
      const x2 = X(a.endDate > end ? end : a.endDate);
      return `<line x1="${x1.toFixed(1)}" x2="${Math.max(x1 + 4, x2).toFixed(1)}" y1="${Y(a.avg).toFixed(1)}" y2="${Y(a.avg).toFixed(1)}" style="stroke:rgb(var(--brand))" stroke-width="3" stroke-linecap="round"/>`;
    }).join('');
    const xl = `<text x="${padL}" y="${H - 6}" font-size="11" fill="#6E6E73">${shortDate(start)}</text><text x="${W - padR}" y="${H - 6}" text-anchor="end" font-size="11" fill="#6E6E73">Today</text>`;
    return `<svg role="img" aria-label="Weigh-ins with 2-week averages" viewBox="0 0 ${W} ${H}" class="w-full mt-3" style="height:auto">${grid}${goalLine}${dots}${steps}${xl}</svg>
      <div class="flex items-center gap-4 mt-2 text-[13px] text-inksoft">
        <span class="inline-flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded-full" style="background:rgb(var(--brand) / 0.45)"></span>Weigh-ins</span>
        <span class="inline-flex items-center gap-1.5"><span class="w-4 h-[3px] rounded" style="background:rgb(var(--brand))"></span>2-week average</span>
      </div>`;
  }

  function checkinCard() {
    const waists = rows.filter((r) => r.waist_in != null);
    const photos = rows.filter((r) => r.photos_taken);
    const lastDate = [waists[0]?.date, photos[0]?.date].filter(Boolean).sort().pop() || null;
    const due = !lastDate || daysBetween(lastDate, todayISO()) >= 14;
    const today = rows.find((r) => r.date === todayISO()) || {};
    return `<div class="grit-card p-5 mt-3">
      <div class="flex items-center justify-between">
        <div class="text-[17px] font-semibold text-ink">Every-2-weeks check-in</div>
        ${due ? '<span class="grit-chip-accent">Due</span>' : `<span class="grit-chip-success">Next ${shortDate(addDaysLocal(lastDate, 14))}</span>`}
      </div>
      <div class="text-[13px] text-inksoft mt-1">Waist at the belly button, relaxed, after you breathe out. Photos front, side and back, same spot and light (p.342-344).</div>
      ${waists.length ? `<div class="text-[15px] text-ink mt-3">Waist <b class="tabular-nums">${waists[0].waist_in}"</b> on ${shortDate(waists[0].date)}${waists[1] ? ` · was ${waists[1].waist_in}" on ${shortDate(waists[1].date)}` : ''}</div>` : ''}
      ${readOnly ? '' : `<div class="flex items-center gap-2 mt-3">
        <input id="waist" type="number" inputmode="decimal" placeholder="${waists[0]?.waist_in ?? 'Waist'}" value="${today.waist_in ?? ''}" aria-label="Waist in inches" class="grit-input flex-1 text-center font-semibold" />
        <span class="text-[15px] text-inksoft">in</span>
        <button id="savewaist" class="grit-btn !w-auto px-6">Save</button>
      </div>
      <button id="photos" class="grit-row w-full flex items-center justify-between mt-3 min-h-[48px] px-4 rounded-xl bg-pagebg text-[15px] text-ink">
        <span>Progress photos taken today</span>
        <span class="w-[51px] h-[31px] rounded-full p-[2px] flex ${today.photos_taken ? 'bg-success justify-end' : 'bg-fill justify-start'} transition-colors"><span class="w-[27px] h-[27px] rounded-full bg-white shadow"></span></span>
      </button>`}
      ${photos.length ? `<div class="text-[13px] text-inksoft mt-2">Last photos ${shortDate(photos[0].date)}. They stay in your phone's camera roll.</div>` : ''}
    </div>`;
  }

  function goalWeightCard() {
    return `<div class="grit-card px-5 mt-3 flex items-center justify-between min-h-[60px]">
      <div class="text-[17px] text-ink">Goal weight</div>
      ${readOnly
        ? `<div class="text-[17px] text-inksoft">${settings.goal_weight_lb ? settings.goal_weight_lb + ' lb' : 'Not set'}</div>`
        : `<div class="flex items-center gap-1.5"><input id="goal-wt" type="number" inputmode="decimal" value="${settings.goal_weight_lb ?? ''}" placeholder="Optional" aria-label="Goal weight in pounds" class="grit-input !w-28 text-center !min-h-[44px] !py-2" /><span class="text-[15px] text-inksoft">lb</span></div>`}
    </div>`;
  }

  function paint() {
    el.innerHTML = `${renderHeader()}
      <div class="grit-page px-5 pt-4">
        <div class="grit-eyebrow">${label(me)}</div>
        <h1 class="grit-title mt-0.5 mb-4">Weight</h1>
        ${planCard()}
        ${logCard()}
        ${trendCard()}
        ${bodyCard()}
        ${checkinCard()}
        ${goalWeightCard()}
      </div>${renderNav('weight')}`;

    const on = (id, fn) => { const n = document.getElementById(id); if (n) n.onclick = fn; };
    on('logwt', handleWeight);
    const wt = document.getElementById('wt'); if (wt) wt.onkeydown = (ev) => { if (ev.key === 'Enter') handleWeight(); };
    on('savewaist', handleWaist);
    on('photos', handlePhotos);
    const gw = document.getElementById('goal-wt'); if (gw) gw.onchange = () => handleGoalWeight(gw.value);
    el.querySelectorAll('[data-goal]').forEach((b) => { b.onclick = () => handlePlan({ diet_goal: b.dataset.goal }); });
    el.querySelectorAll('[data-activity]').forEach((b) => { b.onclick = () => handlePlan({ activity_level: b.dataset.activity }); });
  }

  async function reload() { rows = await loadWeights(me); paint(); }

  async function handleWeight() {
    if (readOnly) return;
    const wtEl = document.getElementById('wt');
    const v = Number(wtEl.value);
    if (!v || v <= 0) { wtEl.focus(); return; }
    const ok = await saveWeight({ date: todayISO(), weight_lb: v });
    if (!ok) { toast("Couldn't save weight, try again", { tone: 'bad' }); return; }
    toast(`Saved ${v}`);
    await reload();
  }

  async function handleWaist() {
    if (readOnly) return;
    const n = document.getElementById('waist');
    const v = Number(n.value);
    if (!v || v <= 0) { n.focus(); return; }
    const ok = await saveCheckin({ date: todayISO(), waist_in: v });
    if (!ok) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
    toast(`Waist ${v}" saved`);
    await reload();
  }

  async function handlePhotos() {
    if (readOnly) return;
    const today = rows.find((r) => r.date === todayISO());
    const ok = await saveCheckin({ date: todayISO(), photos_taken: !today?.photos_taken });
    if (!ok) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
    await reload();
  }

  async function handlePlan(patch) {
    if (readOnly) return;
    if (patch.diet_goal && patch.diet_goal !== goal()) patch.diet_goal_started = todayISO(); // diet-break clock (p.152)
    Object.assign(settings, patch);
    const ok = await saveSettings(patch);
    if (!ok) toast("Couldn't save, try again", { tone: 'bad' });
    paint();
  }

  async function handleGoalWeight(val) {
    if (readOnly) return;
    settings.goal_weight_lb = val === '' ? null : Number(val);
    const ok = await saveSettings({ goal_weight_lb: settings.goal_weight_lb });
    toast(ok ? 'Goal saved' : "Couldn't save, try again", { tone: ok ? 'good' : 'bad' });
    paint();
  }
}

function addDaysLocal(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
